// The shared namespace every bundle hangs off. This file runs first (see
// application.ts / admin.ts) so that everything else can register into it.
//
// The shape mirrors what the layout's inline script sets up in
// layouts/_head.html.erb — vars come from Rails (page name, plus anything set
// with ApplicationController#set_js_var), and the bundles fill in the
// registries.
//
// Two things register here:
//
//   * singletons — site-wide behavior (header, confirm dialogs). Every
//     singleton is initialized on every page.
//   * pages — per-page code, looked up by name. Rails names the current page
//     (`@page_name`, e.g. "PapersManage"), we run `PapersManagePage`.
//
// Because window.Paratelligent lives on the window rather than in module
// scope, the separately-bundled admin code shares one namespace with the
// application code.

export type AppModule = {
  initialize?(): void
  onDomReady?(): void
}

export type Page = AppModule & {
  // Admin pages live in the admin bundle and are only run by admin/main.
  isAdminOnly?: boolean
}

export type Vars = {
  pageName?: string
  [key: string]: unknown
}

export type Namespace = {
  vars: Vars
  singletons: Record<string, AppModule>
  pages: Record<string, Page>
  currentPage: Page | null
}

declare global {
  interface Window {
    Paratelligent?: Namespace
  }
}

// The layout creates this before any bundle loads; the fallback keeps things
// working in tests and on any page that skips the inline script.
export const Paratelligent: Namespace = window.Paratelligent ?? {
  vars: {},
  singletons: {},
  pages: {},
  currentPage: null,
}

window.Paratelligent = Paratelligent

// Singletons are initialized in registration order, which is the import order
// in application.ts.
export function registerSingleton<T extends AppModule>(name: string, singleton: T): T {
  Paratelligent.singletons[name] = singleton
  return singleton
}

// `name` is the page name Rails hands us plus "Page" — e.g. "PapersManagePage"
// for `@page_name = "PapersManage"`.
export function registerPage<T extends Page>(name: string, page: T): T {
  Paratelligent.pages[name] = page
  return page
}

// The page module for this request, or the no-op fallback (pages/page.ts) when
// the current page has no JS of its own.
export function findCurrentPage(): Page | null {
  const pageName = Paratelligent.vars.pageName
  const page = pageName ? Paratelligent.pages[`${pageName}Page`] : undefined
  return page ?? Paratelligent.pages.Page ?? null
}

export function onDomReady(callback: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true })
  } else {
    callback()
  }
}
